import ChangeOrderRequestDetailModel, {
	ChangeOrderRequestSaveModel,
	CheckItemExistRequest,
	CheckItemExistResponse,
	CORequestLineModel,
	IChangeOrderRequestGridDetail,
	IChangeOrderRequestLineAttachment,
	IChangeOrderRequestLineListItem,
	IChangeOrderRequestStepHistory,
	ICompletedStepHistory,
	ICopyCORResult,
	IPreviousCORequestStepHistory,
	IUpdateChangeOrderRequestStepApproveResult,
	ReadyStatusChangeOrderRequestDetail,
	SaveOrUpdateCORequestLineRequest,
} from "models/Budget/ChangeOrderRequest";
import { BaseService } from "./base.service";
import { TableFilterModel } from "models/TableFilterModel";
import {
	IChangeOrderNumberList,
	IChangeOrderRequestNumber,
	IRequester,
	ISourceTransactionId,
} from "models/Budget/ChangeOrderRequestSummaryGridFilterModel";
import { IDelegateToUser } from "models/Requisition/DelegateToUserSelection";
import {
	AddChangeOrderRequestTransferDetail,
	ChangeOrderRequestAdjustmentDetail,
	ChangeOrderRequestAdjustmentSaveModel,
	IChangeOrderRequestAdjustmentGridDetail,
	IChangeOrderRequestTransferDetail,
	IChangeOrderRequestTransferGlAccountDetail,
	IChangeOrderRequestTransferListGridDetail,
} from "models/Budget/ChangeOrderRequestTransferAdjustment";

class ChangeOrderRequestServiceInternal extends BaseService {
	saveChangeOrderRequestDetail = async (
		model: ChangeOrderRequestSaveModel
	): Promise<{ changeOrderRequestId: number }> =>
		this.callApi<{ changeOrderRequestId: number }>(
			`ChangeOrderRequest/Save`,
			"POST",
			undefined,
			model
		);

	updateChangeOrderRequestDetail = async (
		model: ChangeOrderRequestSaveModel
	): Promise<undefined> =>
		this.callApi<undefined>(
			`ChangeOrderRequest/Update/${model.changeOrderRequestId}`,
			"PUT",
			undefined,
			model
		);

	// getChangeOrderRequestDetailById = async (
	// 	changeOrderRequestId: number
	// ): Promise<{ changeOrderRequest: ChangeOrderRequestDetailModel }> =>
	// 	this.callApi<{ changeOrderRequest: ChangeOrderRequestDetailModel }>(
	// 		`ChangeOrderRequest/GetDetailsById/${changeOrderRequestId}`,
	// 		"GET"
	// 	);
	getChangeOrderRequestDetailById = async (
		changeOrderRequestId: number,
		changeOrderRequestStepId?: number | null
	): Promise<ChangeOrderRequestDetailModel> =>
		this.callApi<ChangeOrderRequestDetailModel>(
			`ChangeOrderRequest/GetDetailsById/${changeOrderRequestId}`,
			"GET",
			changeOrderRequestStepId
				? new URLSearchParams({
						changeOrderRequestStepId: changeOrderRequestStepId.toString(),
				  })
				: undefined
		);

	saveChangeOrderRequestLine = async (
		model: SaveOrUpdateCORequestLineRequest
	): Promise<{ changeOrderRequestLineId: number }> =>
		this.callApi<{ changeOrderRequestLineId: number }>(
			`ChangeOrderRequestLine/Save`,
			"POST",
			undefined,
			model
		);

	updateChangeOrderRequestLine = async (
		model: SaveOrUpdateCORequestLineRequest
	): Promise<{ changeOrderRequestLineId: number }> =>
		this.callApi<{ changeOrderRequestLineId: number }>(
			`ChangeOrderRequestLine/Update`,
			"PUT",
			undefined,
			model
		);

	getChangeOrderRequestLineById = async (
		changeOrderRequestLineId: number,
		changeOrderRequestStepId?: number | null
	): Promise<{ changeOrderRequestLineModel: CORequestLineModel }> =>
		this.callApi<{ changeOrderRequestLineModel: CORequestLineModel }>(
			`ChangeOrderRequestLine/GetChangeOrderRequestLineById/${changeOrderRequestLineId}`,
			"GET",
			changeOrderRequestStepId && changeOrderRequestStepId > 0
				? new URLSearchParams({
						changeOrderRequestStepId: changeOrderRequestStepId.toString(),
				  })
				: undefined
		);

	getChangeOrderRequestLineList = async (
		changeOrderRequestId: number,
		changeOrderRequestStepId?: number | null
	): Promise<IChangeOrderRequestLineListItem[]> =>
		this.callApi<IChangeOrderRequestLineListItem[]>(
			`ChangeOrderRequestLine/GetChangeOrderRequestLineList/${changeOrderRequestId}`,
			"GET",
			changeOrderRequestStepId && changeOrderRequestStepId > 0
				? new URLSearchParams({
						changeOrderRequestStepId: changeOrderRequestStepId.toString(),
				  })
				: undefined
		);
	getChangeOrderRequestLineAttachmentList = async (
		changeOrderRequestLineId: number,
		changeOrderRequestStepId?: number | null
	): Promise<IChangeOrderRequestLineAttachment[]> =>
		this.callApi<IChangeOrderRequestLineAttachment[]>(
			`ChangeOrderRequestLineAttachment/GetAttachmentList/${changeOrderRequestLineId}`,
			"GET",
			changeOrderRequestStepId && changeOrderRequestStepId > 0
				? new URLSearchParams({
						changeOrderRequestStepId: changeOrderRequestStepId.toString(),
				  })
				: undefined
		);

	saveChangeOrderRequestLineAttachment = async (
		model: FormData
	): Promise<number> =>
		this.callApi<number>(
			`ChangeOrderRequestLineAttachment/Save`,
			"POST",
			undefined,
			model,
			false,
			true
		);

	updateChangeOrderRequestLineAttachment = async (
		changeOrderRequestLineAttachmentId: number,
		model: FormData
	): Promise<{ changeOrderRequestLineAttachmentId: number }> =>
		this.callApi<{ changeOrderRequestLineAttachmentId: number }>(
			`ChangeOrderRequestLineAttachment/Update/${changeOrderRequestLineAttachmentId}`,
			"PUT",
			undefined,
			model,
			false,
			true
		);

	deleteChangeOrderRequestLineAttachment = async (
		changeOrderRequestLineAttachmentId: number
	): Promise<undefined> =>
		this.callApi<undefined>(
			`ChangeOrderRequestLineAttachment/Delete/${changeOrderRequestLineAttachmentId}`,
			"DELETE",
			undefined,
			undefined,
			true
		);
	checkItemExist = async (
		model: CheckItemExistRequest
	): Promise<CheckItemExistResponse> =>
		this.callApi<CheckItemExistResponse>(
			`ChangeOrderRequestLine/CheckItemExist`,
			"POST",
			undefined,
			model
		);

	deleteChangeOrderRequestLine = async (
		changeOrderRequestLineId: number
	): Promise<null> =>
		this.callApi<null>(
			`ChangeOrderRequestLine/Delete/${changeOrderRequestLineId}`,
			"DELETE"
		);

	getCORequestNumberList = async (
		model: TableFilterModel
	): Promise<IChangeOrderRequestNumber[]> =>
		this.callApi<IChangeOrderRequestNumber[]>(
			`ChangeOrderRequest/GetCORequestNumberList`,
			"POST",
			undefined,
			model
		);

	getAllCONumbers = async (
		model: TableFilterModel
	): Promise<IChangeOrderNumberList[]> =>
		this.callApi<IChangeOrderNumberList[]>(
			`ChangeOrderRequest/GetAllCONumberList`,
			"POST",
			undefined,
			model
		);

	getSourceTransactionIds = async (
		model: TableFilterModel
	): Promise<ISourceTransactionId[]> =>
		this.callApi<ISourceTransactionId[]>(
			`ChangeOrderRequest/GetSourceTransactionIds`,
			"POST",
			undefined,
			model
		);

	getRequesterList = async (model: TableFilterModel): Promise<IRequester[]> =>
		this.callApi<IRequester[]>(
			`ChangeOrderRequest/GetRequesterList`,
			"POST",
			undefined,
			model
		);
	getChangeOrderRequestList = async (
		model: TableFilterModel
	): Promise<IChangeOrderRequestGridDetail[]> =>
		this.callApi<IChangeOrderRequestGridDetail[]>(
			`ChangeOrderRequest/GetChangeOrderRequestList`,
			"POST",
			undefined,
			model
		);
	getOpenFinancialYearStartDate = async (): Promise<string | null> =>
		this.callApi<string | null>(
			`FinancialYear/GetOpenFinancialYearStartDate`,
			"GET"
		);
	submitChangeOrderRequest = async (
		changeOrderRequestId: number,
		comment: string
	) =>
		this.callApi<any>(
			`ChangeOrderRequest/SubmitChangeOrderRequest/${changeOrderRequestId}`,
			"PUT",
			undefined,
			{
				comment: comment,
			}
		);
	getChangeOrderRequestStepList = async (changeOrderRequestId: number) =>
		this.callApi<IChangeOrderRequestStepHistory[]>(
			`ChangeOrderRequestStep/GetChangeOrderRequestStepList/${changeOrderRequestId}`,
			"GET"
		);
	getReadyStatusChangeOrderRequestDetails = async (
		changeOrderRequestId: string,
		changeOrderRequestStepId: string
	): Promise<ReadyStatusChangeOrderRequestDetail> =>
		this.callApi<ReadyStatusChangeOrderRequestDetail>(
			`ChangeOrderRequest/GetReadyStatusCORDetails`,
			"POST",
			undefined,
			{
				Key: changeOrderRequestId.toString(),
				Key_1: changeOrderRequestStepId.toString(),
			}
		);
	updateChangeOrderRequestStepCompleteAndApprove = async (
		changeOrderRequestId: number,
		changeOrderRequestStepId: number,
		comment: string,
		isSubmitToFinance?: boolean
	): Promise<IUpdateChangeOrderRequestStepApproveResult> =>
		this.callApi<IUpdateChangeOrderRequestStepApproveResult>(
			`ChangeOrderRequest/UpdateCORStepCompleteAndApprove/${changeOrderRequestId}/${changeOrderRequestStepId}`,
			"POST",
			isSubmitToFinance === true
				? new URLSearchParams({
						isSubmitToFinance: isSubmitToFinance.toString(),
				  })
				: undefined,
			{ comment }
		);
	rejectChangeOrderRequest = async (
		changeOrderRequestId: number,
		changeOrderRequestStepId: number,
		comment: string
	): Promise<IUpdateChangeOrderRequestStepApproveResult> =>
		this.callApi<IUpdateChangeOrderRequestStepApproveResult>(
			`ChangeOrderRequest/RejectChangeOrderRequest/${changeOrderRequestId}/${changeOrderRequestStepId}`,
			"POST",
			undefined,
			{ comment }
		);
	checkIsChangeOrderRequestStepHistoryReady = async (
		changeOrderRequestStepId: number
	): Promise<boolean> =>
		this.callApi<boolean>(
			`ChangeOrderRequest/CheckIsChangeOrderRequestStepHistoryReady/${changeOrderRequestStepId}`,
			"GET"
		);

	getStepsBeforeCurrentStep = async (
		changeOrderRequestId: number,
		currentStepCORStepId: number
	) =>
		this.callApi<IPreviousCORequestStepHistory[]>(
			`ChangeOrderRequestStep/GetStepsBeforeCurrentStep/${changeOrderRequestId}/${currentStepCORStepId}`,
			"GET"
		);

	getCompletedChangeOrderRequestStepDetail = async (
		changeOrderRequestId: number
	) =>
		this.callApi<ICompletedStepHistory>(
			`ChangeOrderRequestStep/GetCompletedChangeOrderRequestStepDetail/${changeOrderRequestId}`,
			"GET"
		);
	updateChangeOrderRequestStepStatusChangeRequest = async (
		changeOrderRequestId: number,
		selectedStepChangeOrderRequestStepHistoryId: number,
		currentStepChangeOrderRequestStepHistoryId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`ChangeOrderRequest/UpdateCORStepStatusChangeRequest/${changeOrderRequestId}`,
			"POST",
			undefined,
			{
				comment: comment,
				selectedStepCORStepId:
					selectedStepChangeOrderRequestStepHistoryId.toString(),
				currentStepCORStepId:
					currentStepChangeOrderRequestStepHistoryId.toString(),
			}
		);
	getDelegatedUserList = async (
		changeOrderRequestStepId: number
	): Promise<IDelegateToUser[]> =>
		this.callApi<IDelegateToUser[]>(
			`ChangeOrderRequest/GetDelegatedUserList/${changeOrderRequestStepId}`,
			"GET"
		);
	delegateChangeOrderRequest = async (
		changeOrderRequestId: number,
		changeOrderRequestStepId: number,
		userId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`ChangeOrderRequest/DelegateChangeOrderRequest/${changeOrderRequestId}/${changeOrderRequestStepId}`,
			"POST",
			undefined,
			{
				comment: comment,
				userId: userId.toString(),
			}
		);
	updateChangeOrderRequestStepStatusRecall = async (
		changeOrderRequestId: number,
		completedStepchangeOrderRequestStepHistoryId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`ChangeOrderRequest/UpdateCORStepStatusRecall/${changeOrderRequestId}`,
			"POST",
			undefined,
			{
				completedStepCORStepId:
					completedStepchangeOrderRequestStepHistoryId.toString(),
				comment: comment,
			}
		);
	reSubmitChangeOrderRequest = async (
		changeOrderRequestId: number,
		currentStepChangeOrderRequestStepHistoryId: number,
		comment: string
	): Promise<undefined> =>
		this.callApi<undefined>(
			`ChangeOrderRequest/ReSubmitChangeOrderRequest/${changeOrderRequestId}/${currentStepChangeOrderRequestStepHistoryId}`,
			"PUT",
			undefined,
			{
				comment: comment,
			}
		);
	copyChangeOrderRequest = async (
		changeOrderRequestId: number
	): Promise<ICopyCORResult> =>
		this.callApi<ICopyCORResult>(
			`ChangeOrderRequest/Copy/${changeOrderRequestId}`,
			"POST"
		);
	getChangeOrderRequestTransferDetail = async (
		changeOrderRequestLineDetailId: number
	): Promise<IChangeOrderRequestTransferDetail> =>
		this.callApi<IChangeOrderRequestTransferDetail>(
			`ChangeOrderRequestTransfer/GetChangeOrderRequestTransferDetail/${changeOrderRequestLineDetailId}`,
			"GET"
		);
	getChangeOrderRequestTransferList = async (
		changeOrderRequestLineDetailId: number
	): Promise<IChangeOrderRequestTransferListGridDetail[]> =>
		this.callApi<IChangeOrderRequestTransferListGridDetail[]>(
			`ChangeOrderRequestTransfer/GetChangeOrderRequestTransferList/${changeOrderRequestLineDetailId}`,
			"GET"
		);
	getChangeOrderTransferItemList = async (
		changeOrderRequestLineDetailId: number,
		departmentId: number,
		queryParams?: {
			isFromAdjustment?: boolean;
			divisionId?: number | null;
			regionId?: number | null;
			subDivisionId?: number | null;
			subRegionId?: number | null;
			departmentInstanceId?: number | null;
			subDepartmentId?: number | null;
			portfolioOrganizationUnitId?: number | null;
			projectOrganizationUnitId?: number | null;
			costCategoryId?: number | null;
		}
	): Promise<IChangeOrderRequestTransferGlAccountDetail[]> => {
		const params = new URLSearchParams();
		if (queryParams) {
			Object.entries(queryParams).forEach(([key, value]) => {
				if (value !== undefined && value !== null) {
					params.append(key, value.toString());
				}
			});
		}
		return this.callApi<IChangeOrderRequestTransferGlAccountDetail[]>(
			`ChangeOrderRequestTransfer/GetChangeOrderRequestTransferGlAndItemList/${changeOrderRequestLineDetailId}/${departmentId}`,
			"GET",
			params.toString() ? params : undefined
		);
	};
	saveChangeOrderRequestTransfer = async (
		model: AddChangeOrderRequestTransferDetail
	): Promise<boolean> =>
		this.callApi<boolean>(
			`ChangeOrderRequestTransfer/Save`,
			"POST",
			undefined,
			model
		);
	deleteChangeOrderRequestTransferHistory = async (
		changeOrderRequestTransferHistoryId: number
	) =>
		this.callApi(
			`ChangeOrderRequestTransfer/DeleteByCORTransferHistoryId/${changeOrderRequestTransferHistoryId}`,
			"DELETE"
		);
	getChangeOrderRequestAdjustmentDetail = async (
		changeOrderRequestLineDetailId: number
	): Promise<ChangeOrderRequestAdjustmentDetail> =>
		this.callApi<ChangeOrderRequestAdjustmentDetail>(
			`ChangeOrderRequestAdjustment/GetChangeOrderRequestAdjustmentDetail/${changeOrderRequestLineDetailId}`,
			"GET"
		);
	deleteChangeOrderRequestAdjustmentByHistoryId = async (
		changeOrderRequestAdjustmentHistoryId: number,
		changeOrderRequestLineDetailId: number
	) =>
		this.callApi(
			`ChangeOrderRequestAdjustment/DeleteByCORAdjustmentHistoryId/${changeOrderRequestAdjustmentHistoryId}/${changeOrderRequestLineDetailId}`,
			"DELETE"
		);
	saveChangeOrderRequestAdjustment = async (
		model: ChangeOrderRequestAdjustmentSaveModel
	): Promise<number> =>
		this.callApi<number>(
			`ChangeOrderRequestAdjustment/Save`,
			"POST",
			undefined,
			model
		);
	getAdjustmentListByLineDetailId = async (
		changeOrderRequestLineDetailId: number
	): Promise<IChangeOrderRequestAdjustmentGridDetail[]> =>
		this.callApi<IChangeOrderRequestAdjustmentGridDetail[]>(
			`ChangeOrderRequestAdjustment/GetChangeOrderRequestAdjustmentList/${changeOrderRequestLineDetailId}`,
			"GET"
		);
}
export const ChangeOrderRequestService =
	new ChangeOrderRequestServiceInternal();
